import { Badge, Checkbox, Group, Text, Tooltip } from '@mantine/core';
import type { AssetTree, GrantNode, WellStatus } from '@twinlink/shared';
import { useTranslation } from 'react-i18next';
import { FiDroplet, FiLayers } from 'react-icons/fi';
import { NavLink } from 'react-router-dom';
import classes from './AssetTreeView.module.css';

export const STATUS_COLOR: Record<WellStatus, string> = {
  testing: '#17a35a',
  standby: '#3454d1',
  rigup: '#c77700',
  offline: '#64748b',
  demob: '#94a3b8',
};

/** Status as a coloured dot plus its label (never colour alone). */
export function WellStatusLabel({ status, compact }: { status: WellStatus; compact?: boolean }) {
  const { t } = useTranslation();
  const dot = <span className={classes.dot} style={{ background: STATUS_COLOR[status] }} aria-hidden />;
  if (compact) {
    return (
      <Tooltip label={t(`wellsites.status.${status}`)}>
        <span role="img" aria-label={t(`wellsites.status.${status}`)} style={{ display: 'inline-flex' }}>
          {dot}
        </span>
      </Tooltip>
    );
  }
  return (
    <Group gap={6} wrap="nowrap" component="span">
      {dot}
      <Text span size="sm">
        {t(`wellsites.status.${status}`)}
      </Text>
    </Group>
  );
}

/** Field → Well tree with links to the detail pages. */
export function AssetTreeNav({ tree, basePath }: { tree: AssetTree; basePath: string }) {
  const { t } = useTranslation();
  const link = (to: string) => ({ to: `${basePath}/${to}`, className: ({ isActive }: { isActive: boolean }) => `${classes.row} ${isActive ? classes.active : ''}` });
  return (
    <ul className={classes.tree} aria-label={t('wellsites.tree')}>
      {tree.fields.map((f) => (
        <li key={f.id}>
          <NavLink {...link(`field/${f.id}`)}>
            <FiLayers size={14} aria-hidden />
            <span className={classes.label}>{f.name}</span>
          </NavLink>
          <ul>
            {f.wells.map((w) => (
              <li key={w.id}>
                <NavLink {...link(`well/${w.id}`)}>
                  <FiDroplet size={14} aria-hidden />
                  <span className={classes.label}>{w.name}</span>
                  <WellStatusLabel status={w.status} compact />
                  {w.runningTest && (
                    <Badge size="xs" color="green">
                      {t('wellsites.testing')}
                    </Badge>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

const key = (g: GrantNode) => `${g.nodeType}:${g.nodeId}`;

/**
 * Grant picker (doc 02 §5.4): checking a field grants all its wells, now and later, so its
 * wells show as checked and locked.
 */
export function GrantPicker({ tree, value, onChange, disabled }: { tree: AssetTree; value: GrantNode[]; onChange: (grants: GrantNode[]) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const selected = new Set(value.map(key));
  const toggle = (node: GrantNode, on: boolean) => onChange(on ? [...value, node] : value.filter((g) => key(g) !== key(node)));

  if (tree.fields.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        {t('wellsites.grants.noAssets')}
      </Text>
    );
  }
  return (
    <ul className={classes.tree} aria-label={t('wellsites.grants.picker')}>
      {tree.fields.map((f) => {
        const fieldOn = selected.has(`field:${f.id}`);
        return (
          <li key={f.id}>
            <div className={classes.row}>
              <Checkbox size="xs" label={f.name} checked={fieldOn} disabled={disabled} onChange={(e) => toggle({ nodeType: 'field', nodeId: f.id }, e.currentTarget.checked)} />
              {fieldOn && <Badge size="xs">{t('wellsites.grants.wholeField')}</Badge>}
            </div>
            <ul>
              {f.wells.map((w) => (
                <li key={w.id}>
                  <div className={classes.row}>
                    <Checkbox
                      size="xs"
                      label={w.name}
                      checked={fieldOn || selected.has(`well:${w.id}`)}
                      disabled={disabled || fieldOn}
                      onChange={(e) => toggle({ nodeType: 'well', nodeId: w.id }, e.currentTarget.checked)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </li>
        );
      })}
    </ul>
  );
}

/** Wells a grant set gives access to, as "Field / Well" labels (the effective preview). */
export function effectiveWells(tree: AssetTree, grants: GrantNode[]): string[] {
  const selected = new Set(grants.map(key));
  const out: string[] = [];
  for (const f of tree.fields) {
    for (const w of f.wells) {
      if (selected.has(`field:${f.id}`) || selected.has(`well:${w.id}`)) out.push(`${f.name} / ${w.name}`);
    }
  }
  return out;
}

/** Drops grants made redundant by a checked field, so the stored set stays minimal. */
export function normalizeGrants(tree: AssetTree, grants: GrantNode[]): GrantNode[] {
  const selected = new Set(grants.map(key));
  const out: GrantNode[] = [];
  for (const f of tree.fields) {
    if (selected.has(`field:${f.id}`)) {
      out.push({ nodeType: 'field', nodeId: f.id });
      continue;
    }
    for (const w of f.wells) if (selected.has(`well:${w.id}`)) out.push({ nodeType: 'well', nodeId: w.id });
  }
  return out;
}
